import { useCallback } from 'react'
import * as Sentry from '@sentry/nextjs'

import { toaster } from '@/components/Toaster'

import { useAllAlerts } from '@/feature/alerts/api/useAllAlerts'
import { useDeleteAlert } from '@/feature/alerts/api/useDeleteAlert'
import { useAlertStore } from '@/feature/alerts/store'
import { isAlertMissingError, removeAlertById } from '@/feature/alerts/utils'

export const useConfirmDeleteAlert = () => {
  const { isDeleteAlertOpen, closeDeleteAlert, alertToDelete } = useAlertStore()
  const { trigger, isMutating } = useDeleteAlert()
  const { mutate: mutateAlerts } = useAllAlerts()

  const handleDelete = useCallback(async () => {
    if (!alertToDelete) return

    const { id } = alertToDelete

    try {
      await trigger({ params: { id } })
    } catch (error) {
      // Anything other than "it is already gone" is a real failure the user has
      // to hear about — previously it rejected unhandled, so the button simply
      // flipped back to "Delete" with no explanation.
      if (!isAlertMissingError(error)) {
        console.error(error)
        Sentry.captureException(error, {
          tags: { feature: 'alerts', api: 'v1/alerts/:id/' },
          extra: { alertId: id, method: 'delete' },
        })
        toaster({ message: 'Failed to delete alert. Please try again.', type: 'error' })
        return
      }
    }

    // The alert is gone on the backend, so drop it from the cache directly.
    // Revalidating first made both the dialog closing and the alert disappearing
    // hostage to that GET succeeding: when it failed, the alert stayed listed and
    // the dialog stayed open, which read as a failed delete (BRI-1517).
    await mutateAlerts((current) => removeAlertById(current, id), { revalidate: false })

    closeDeleteAlert()
    toaster({ message: 'Alert deleted', type: 'success' })

    // Reconcile with the backend afterwards. Deliberately not awaited: a failure
    // here must not block the close or resurrect the alert we just removed.
    void mutateAlerts().catch((error) => {
      console.error(error)
      Sentry.captureException(error, {
        tags: { feature: 'alerts', api: 'v1/alerts/' },
        extra: { alertId: id, context: 'post-delete revalidation' },
      })
    })
  }, [alertToDelete, closeDeleteAlert, mutateAlerts, trigger])

  return { isDeleteAlertOpen, closeDeleteAlert, alertToDelete, isMutating, handleDelete }
}
